import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Bell, RefreshCw, AlertCircle, Plus } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { toast } from '../components/ui/Toast';
import {
  api,
  type SavedSearch,
  type Listing,
  type Lead,
  type LeadStatus,
  ApiError,
} from '../services/api';
import { useAuthStore } from '../stores/auth';
import { useListingsStore } from '../stores/listings';
import { DashboardStats } from '../components/dashboard/DashboardStats';
import { SearchCard } from '../components/dashboard/SearchCard';
import { SaveAsLeadDialog } from '../components/dashboard/SaveAsLeadDialog';
import { LeadCard } from '../components/dashboard/LeadCard';
import { LeadFilters } from '../components/dashboard/LeadFilters';
import { NewSearchDialog } from '../components/dashboard/NewSearchDialog';
import { usePushNotifications } from '../hooks/usePushNotifications';

type TabId = 'searches' | 'leads';

export default function SavedSearches() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuthStore();
  const { setFilters, fetchListings } = useListingsStore();
  const { subscribe: subscribePush, isSubscribed: isPushSubscribed } = usePushNotifications();

  // Tab state
  const initialTab = (searchParams.get('tab') as TabId) || 'searches';
  const [activeTab, setActiveTab] = useState<TabId>(initialTab);

  // Searches state
  const [searches, setSearches] = useState<SavedSearch[]>([]);
  const [isLoadingSearches, setIsLoadingSearches] = useState(true);
  const [searchesError, setSearchesError] = useState<string | null>(null);
  const [expandedSearchId, setExpandedSearchId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Leads state
  const [leads, setLeads] = useState<Lead[]>([]);
  const [leadsTotal, setLeadsTotal] = useState(0);
  const [isLoadingLeads, setIsLoadingLeads] = useState(false);
  const [leadStats, setLeadStats] = useState<Record<string, number>>({});
  const [leadFilter, setLeadFilter] = useState<LeadStatus | undefined>();
  const [leadSearch, setLeadSearch] = useState('');

  // "Save as lead" dialog
  const [saveLeadListing, setSaveLeadListing] = useState<Listing | null>(null);
  const [saveLeadSearchId, setSaveLeadSearchId] = useState<string | undefined>();
  const [saveLeadDialogOpen, setSaveLeadDialogOpen] = useState(false);

  // Track saved external IDs for dedup badges
  const [savedExternalIds, setSavedExternalIds] = useState<Set<string>>(new Set());

  // New search dialog
  const [newSearchDialogOpen, setNewSearchDialogOpen] = useState(false);

  // Auth redirect
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  // Fetch searches
  const fetchSearches = useCallback(async () => {
    setIsLoadingSearches(true);
    setSearchesError(null);
    try {
      const { searches } = await api.getSavedSearches();
      setSearches(searches);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Errore nel caricamento';
      setSearchesError(msg);
    } finally {
      setIsLoadingSearches(false);
    }
  }, []);

  // Fetch leads
  const fetchLeads = useCallback(async () => {
    setIsLoadingLeads(true);
    try {
      const result = await api.getLeads({
        status: leadFilter,
        search: leadSearch || undefined,
        limit: 50,
      });
      setLeads(result.leads);
      setLeadsTotal(result.total);

      // Update saved external IDs set
      const ids = new Set<string>();
      for (const lead of result.leads) {
        if (lead.externalListingId) ids.add(lead.externalListingId);
      }
      setSavedExternalIds(ids);
    } catch {
      // Silently fail - leads are secondary
    } finally {
      setIsLoadingLeads(false);
    }
  }, [leadFilter, leadSearch]);

  // Fetch lead stats
  const fetchLeadStats = useCallback(async () => {
    try {
      const { stats } = await api.getLeadStats();
      setLeadStats(stats);
    } catch {
      // Silently fail
    }
  }, []);

  // Migrate localStorage leads (one-time)
  useEffect(() => {
    if (!isAuthenticated) return;

    const stored = localStorage.getItem('homefinder_leads');
    if (!stored) return;

    try {
      const localLeads = JSON.parse(stored);
      if (!Array.isArray(localLeads) || localLeads.length === 0) return;

      const statusMap: Record<string, LeadStatus> = {
        new: 'NEW',
        contacted: 'CONTACTED',
        interested: 'INTERESTED',
        not_interested: 'NOT_INTERESTED',
        converted: 'CONVERTED',
      };

      Promise.allSettled(
        localLeads.map((l: any) =>
          api.createLead({
            name: l.name || 'Lead senza nome',
            phone: l.phone || undefined,
            email: l.email || undefined,
            propertyAddress: l.propertyInterest || undefined,
          }).then(async ({ lead }) => {
            // Migrate notes
            if (l.notes && Array.isArray(l.notes)) {
              for (const note of l.notes) {
                try {
                  await api.addLeadNote(lead.id, {
                    text: note.text,
                    isCallAttempt: note.isCallAttempt || false,
                  });
                } catch {
                  // Skip failed notes
                }
              }
            }
            // Update status if not NEW
            if (l.status && statusMap[l.status] && statusMap[l.status] !== 'NEW') {
              try {
                await api.updateLead(lead.id, { status: statusMap[l.status] });
              } catch {
                // Skip
              }
            }
          })
        )
      ).then((results) => {
        const succeeded = results.filter((r) => r.status === 'fulfilled').length;
        if (succeeded > 0) {
          localStorage.removeItem('homefinder_leads');
          toast({
            title: `${succeeded} lead migrati nel tuo account`,
            variant: 'success',
          });
          fetchLeads();
          fetchLeadStats();
        }
      });
    } catch {
      // Invalid JSON, remove
      localStorage.removeItem('homefinder_leads');
    }
  }, [isAuthenticated]);

  // Initial data fetch
  useEffect(() => {
    if (!isAuthenticated) return;
    fetchSearches();
    fetchLeads();
    fetchLeadStats();
  }, [isAuthenticated, fetchSearches, fetchLeads, fetchLeadStats]);

  // Refetch leads when filters change
  useEffect(() => {
    if (!isAuthenticated) return;
    fetchLeads();
  }, [leadFilter, leadSearch, isAuthenticated, fetchLeads]);

  // Tab change
  const handleTabChange = (tab: TabId) => {
    setActiveTab(tab);
    setSearchParams(tab === 'searches' ? {} : { tab });
  };

  // Search actions
  const handleToggleNotifications = async (search: SavedSearch) => {
    try {
      const enabling = !search.notifyEnabled;

      // If enabling and not yet subscribed to push, subscribe first
      if (enabling && !isPushSubscribed) {
        const result = await subscribePush();
        if (!result.ok) {
          toast({ title: 'Notifiche non disponibili', description: result.message, variant: 'error' });
          return;
        }
      }

      await api.updateSavedSearch(search.id, { notifyEnabled: enabling });
      setSearches((prev) =>
        prev.map((s) =>
          s.id === search.id ? { ...s, notifyEnabled: enabling } : s
        )
      );
      toast({
        title: enabling ? 'Notifiche attivate' : 'Notifiche disattivate',
        variant: 'success',
      });
    } catch {
      toast({ title: 'Errore aggiornamento notifiche', variant: 'error' });
    }
  };

  const handleDeleteSearch = async (search: SavedSearch) => {
    setDeletingId(search.id);
    try {
      await api.deleteSavedSearch(search.id);
      setSearches((prev) => prev.filter((s) => s.id !== search.id));
      toast({ title: 'Ricerca eliminata', variant: 'success' });
    } catch {
      toast({ title: 'Errore eliminazione', variant: 'error' });
    } finally {
      setDeletingId(null);
    }
  };

  const handleRunSearch = (search: SavedSearch) => {
    setFilters(search.filters);
    fetchListings();
    navigate('/listings');
  };

  // Lead actions
  const handleSaveAsLead = (listing: Listing, searchId: string) => {
    setSaveLeadListing(listing);
    setSaveLeadSearchId(searchId);
    setSaveLeadDialogOpen(true);
  };

  const handleLeadCreated = () => {
    fetchLeads();
    fetchLeadStats();
  };

  // Computed values
  const activeAlerts = searches.filter((s) => s.notifyEnabled).length;
  const newLeads = leadStats['NEW'] || 0;

  // Loading state
  if (isLoadingSearches && leads.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-6">
          <div className="h-7 w-44 bg-gray-200 rounded-lg animate-pulse mb-6" />
          <div className="grid grid-cols-3 gap-3 mb-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-white rounded-2xl shadow-sm p-4 h-24 animate-pulse" />
            ))}
          </div>
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-white rounded-2xl shadow-sm p-4 h-20 animate-pulse" />
            ))}
          </div>
        </div>
        <div className="h-20 md:h-0" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl font-semibold text-gray-900">I tuoi Alert</h1>
          <Button size="sm" className="h-9" onClick={() => setNewSearchDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Nuova ricerca
          </Button>
        </div>

        {/* Stats */}
        <div className="mb-6">
          <DashboardStats
            totalSearches={searches.length}
            activeAlerts={activeAlerts}
            totalLeads={leadStats.total || 0}
            newLeads={newLeads}
          />
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-5 bg-gray-100 rounded-xl p-1">
          <button
            type="button"
            onClick={() => handleTabChange('searches')}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'searches'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Search className="h-4 w-4 inline mr-1.5 -mt-0.5" />
            Ricerche ({searches.length})
          </button>
          <button
            type="button"
            onClick={() => handleTabChange('leads')}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'leads'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Bell className="h-4 w-4 inline mr-1.5 -mt-0.5" />
            Lead ({leadsTotal})
          </button>
        </div>

        {/* Tab: Ricerche */}
        {activeTab === 'searches' && (
          <>
            {searchesError ? (
              <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
                <div className="flex justify-center mb-4">
                  <div className="rounded-full bg-red-100 p-4">
                    <AlertCircle className="h-8 w-8 text-red-500" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Errore</h3>
                <p className="text-gray-500 mb-6 max-w-sm mx-auto">{searchesError}</p>
                <Button onClick={fetchSearches}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Riprova
                </Button>
              </div>
            ) : searches.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12 text-center">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gray-100 flex items-center justify-center">
                  <Search className="h-10 w-10 text-gray-400" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  Nessuna ricerca salvata
                </h2>
                <p className="text-gray-500 mb-8 max-w-sm mx-auto">
                  Salva le tue ricerche per ricevere notifiche sui nuovi annunci e convertirli in lead
                </p>
                <Button size="lg" className="h-12" onClick={() => setNewSearchDialogOpen(true)}>
                  <Plus className="mr-2 h-5 w-5" />
                  Crea la tua prima ricerca
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {searches.map((search) => (
                  <SearchCard
                    key={search.id}
                    search={search}
                    isExpanded={expandedSearchId === search.id}
                    onToggleExpand={() =>
                      setExpandedSearchId(expandedSearchId === search.id ? null : search.id)
                    }
                    onToggleNotifications={handleToggleNotifications}
                    onDelete={handleDeleteSearch}
                    onRunSearch={handleRunSearch}
                    onSaveAsLead={handleSaveAsLead}
                    savedExternalIds={savedExternalIds}
                    isDeleting={deletingId === search.id}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* Tab: Lead */}
        {activeTab === 'leads' && (
          <>
            <div className="mb-4">
              <LeadFilters
                search={leadSearch}
                onSearchChange={setLeadSearch}
                status={leadFilter}
                onStatusChange={setLeadFilter}
              />
            </div>

            {isLoadingLeads && leads.length === 0 ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="bg-white rounded-2xl shadow-sm p-4 h-20 animate-pulse" />
                ))}
              </div>
            ) : leads.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                  <Bell className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {leadSearch || leadFilter ? 'Nessun risultato' : 'Nessun lead'}
                </h3>
                <p className="text-gray-500 text-sm max-w-sm mx-auto">
                  {leadSearch || leadFilter
                    ? 'Prova con un altro filtro o termine di ricerca'
                    : 'Espandi una ricerca salvata e clicca "Salva come Lead" per iniziare'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {leads.map((lead) => (
                  <LeadCard key={lead.id} lead={lead} onUpdate={handleLeadCreated} />
                ))}
              </div>
            )}
          </>
        )}

        {/* Save as Lead Dialog */}
        <SaveAsLeadDialog
          open={saveLeadDialogOpen}
          onOpenChange={setSaveLeadDialogOpen}
          listing={saveLeadListing}
          searchId={saveLeadSearchId}
          onLeadCreated={handleLeadCreated}
        />

        {/* New Search Dialog */}
        <NewSearchDialog
          open={newSearchDialogOpen}
          onOpenChange={setNewSearchDialogOpen}
          onSearchCreated={fetchSearches}
        />
      </div>

      {/* Spacer for mobile nav */}
      <div className="h-20 md:h-0" />
    </div>
  );
}
